import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { SortDirection } from "@angular/material/sort";
import { Logger, UntilDestroy, untilDestroyed } from "@app/@core";
import { AppConstants } from "@app/@shared/app-constants";
import {
  DefaultFilterForm,
  defaultFilterFormValue,
} from "@app/@shared/models/_common/filter-forms/default-filter-form";
import { RequestParams } from "@app/@shared/models/_results";
import { Housing, HousingFields } from "@app/@shared/models/housing";
import { NotificationService } from "@app/@shared/services/notification.service";
import { HousingService } from "@app/housing/housing.service";
import { Subject } from "rxjs";
import { debounceTime, finalize } from "rxjs/operators";

interface HousingsReadFormGroup extends DefaultFilterForm {
  tenantId: FormControl<number | null>;
  sortField: FormControl<string>;
  sortDirection: FormControl<SortDirection>;
}

@UntilDestroy()
@Component({
  selector: "app-house-selector[control]",
  templateUrl: "./house-selector.component.html",
  styleUrls: ["./house-selector.component.scss"],
})
export class HouseSelectorComponent implements OnInit {
  @Input() control: FormControl<Housing | null>;
  @Input() label = "housings.housing";

  @Input() set tenantId(value: number) {
    this.form.controls.tenantId.setValue(value);
  }

  // groupId con setter para reaccionar a cambios
  private _groupId: number | null = null;
  @Input() set groupId(value: number | null) {
    if (this._groupId === value) return; // evita recargas redundantes
    this._groupId = value;
    this._onGroupChanged();
  }
  get groupId(): number | null {
    return this._groupId;
  }

  @Input() showOnlyHousesWithTenants = false;
  @Input() hasClearButton = false;
  @Input() hasRefreshButton = true;

  @Input() public set isTouch(isTouch: false) {
    this._isTouch = isTouch;
    if (this._isTouch == true) {
      this.control.markAllAsTouched();
    }
  }
  @Output() closeButtonClicked = new EventEmitter<void>();

  itemList: Housing[] = [];
  hasMoreItems: boolean;
  peopleInput$ = new Subject<string>();
  isLoading: boolean;
  HousingFields = HousingFields;
  private _isTouch: any;

  // Texto mostrado
  displayText = "";

  form = new FormGroup<HousingsReadFormGroup>({
    ...defaultFilterFormValue,
    sortField: new FormControl<string>(HousingFields.description, {
      nonNullable: true,
    }),
    sortDirection: new FormControl<SortDirection>("asc"),
    tenantId: new FormControl<number>(null),
  });

  log = new Logger(this.constructor.name);

  constructor(
    private housesService: HousingService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    // Entrada de términos de búsqueda
    this.peopleInput$
      .pipe(untilDestroyed(this))
      .subscribe((term) => {
        this.log.debug("term", term);
        this.form.patchValue({
          query: term,
          pageSize: AppConstants.DEFAULT_PAGE_SIZE,
        });
      });

    // Reaccionar a cambios del formulario interno
    this.form.valueChanges
      .pipe(debounceTime(AppConstants.DEFAULT_DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe(() => {
        this.log.debug("form", this.form.getRawValue());
        this._loadData();
      });

    // Sincronizar texto mostrado con valor externo
    this.control.valueChanges
      ?.pipe(untilDestroyed(this))
      .subscribe((v) => (this.displayText = v ? this.getShowedText(v) : ""));

    // Inicial
    this.form.patchValue({});
    this.displayText = this.control.value ? this.getShowedText(this.control.value) : "";
  }

  // Reacción al cambio de grupo
  private _onGroupChanged() {
    // Limpia selección y texto
    this.control?.reset(null, { emitEvent: false });
    this.displayText = "";

    // Reinicia paginación y query
    this.form.patchValue(
      {
        query: "",
        pageIndex: 0,
        pageSize: AppConstants.DEFAULT_PAGE_SIZE,
      },
      { emitEvent: false }
    );

    // Carga si hay grupo; si no, vacía la lista
    if (this._groupId != null) {
      this._loadData();
    } else {
      this.itemList = [];
      this.hasMoreItems = false;
    }
  }

  isRequired(): boolean {
    return this.control.hasValidator(Validators.required);
  }

  compareWith(a: Housing, b: Housing): boolean {
    return !!a && !!b && a.id === b.id;
  }

  onScrollToEnd() {
    if (this.hasMoreItems) {
      const pageSize: number =
        this.form.value.pageSize + AppConstants.DEFAULT_PAGE_SIZE;
      this.form.patchValue({ pageSize });
    } else {
      this.log.debug("No more items");
    }
  }

  onCloseButtonClicked() {
    this.control.reset();
    this.displayText = "";
    this.closeButtonClicked.emit();
  }

  onRefreshButtonClicked() {
    this._loadData();
  }

  onOptionSelected(item: Housing) {
    this.control.setValue(item);
    this.displayText = this.getShowedText(item);
  }

  getShowedText(item: Housing): string {
    const hasAbbreviation =
      item?.abbreviation && item.abbreviation.trim() !== "";
    const abbreviation = hasAbbreviation ? `[${item.abbreviation.trim()}] ` : "";
    const descriptionPart = item?.description?.startsWith("GL")
      ? `${item.description}, `
      : "";
    return `${abbreviation}${descriptionPart}${item?.full_street ?? ""}`.trim();
  }

  private _loadData(): void {
    if (this.form.invalid) {
      this.log.warn("Trying to load data with invalid form");
      return;
    }

    this.isLoading = true;
    const formValue = this.form.getRawValue();

    const param = new RequestParams({
      query: {
        fields: [
          HousingFields.description,
          HousingFields.street,
          HousingFields.full_street,
          HousingFields.abbreviation,
        ],
        value: formValue.query,
      },
      pagination: {
        pageIndex: formValue.pageIndex,
        pageSize: formValue.pageSize,
      },
      order: [{ field: formValue.sortField, type: formValue.sortDirection }],
      filter: [
        ...(formValue.tenantId != null
          ? [
              {
                field: `${HousingFields.tenants}.id__exact`,
                value: formValue.tenantId.toString(),
              },
            ]
          : this.showOnlyHousesWithTenants
          ? [
              {
                field: `${HousingFields.tenants}.id__gt`,
                value: "-1",
              },
            ]
          : []),
        ...(this.groupId != null
          ? [{ field: `${HousingFields.group_id}`, value: this.groupId.toString() }]
          : []),
      ],
    });

    this.housesService
      .getHouses(param)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (result) => {
          this.itemList = result.items;
          this.hasMoreItems = result.total > result.items.length;
        },
        error: () => {
          this.notificationService.openEntityListLoadErrorNotification(
            "houses.houses",
          );
        },
      });
  }
}
